/**
 * Offline action queue for the daily checklist + attendance.
 * Uses IndexedDB (via idb-keyval if present, otherwise a tiny in-house wrapper).
 * Actions are enqueued when the network fails; a background sync loop retries them.
 */

const DB_NAME = "flexworx-offline";
const STORE = "queue";
const KEY_INDEX = "_index";

async function openDb() {
    return await new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet(key) {
    const db = await openDb();
    return new Promise((r, j) => {
        const tx = db.transaction(STORE, "readonly");
        const g = tx.objectStore(STORE).get(key);
        g.onsuccess = () => r(g.result);
        g.onerror = () => j(g.error);
    });
}
async function idbSet(key, value) {
    const db = await openDb();
    return new Promise((r, j) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(value, key);
        tx.oncomplete = () => r();
        tx.onerror = () => j(tx.error);
    });
}
async function idbDel(key) {
    const db = await openDb();
    return new Promise((r, j) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).delete(key);
        tx.oncomplete = () => r();
        tx.onerror = () => j(tx.error);
    });
}

let _listeners = new Set();
export function onQueueChange(cb) { _listeners.add(cb); return () => _listeners.delete(cb); }
async function fireChange() {
    const items = await peek();
    _listeners.forEach((cb) => cb({ pending: items.length, items }));
}

async function pushAction(action) {
    const idx = (await idbGet(KEY_INDEX)) || [];
    const id = crypto.randomUUID();
    idx.push(id);
    await idbSet(id, { id, ...action, attempts: 0, created_at: Date.now() });
    await idbSet(KEY_INDEX, idx);
    fireChange();
    return id;
}

export async function peek() {
    const idx = (await idbGet(KEY_INDEX)) || [];
    const items = [];
    for (const id of idx) {
        const it = await idbGet(id);
        if (it) items.push(it);
    }
    return items;
}

export async function clearAll() {
    const idx = (await idbGet(KEY_INDEX)) || [];
    for (const id of idx) await idbDel(id);
    await idbSet(KEY_INDEX, []);
    fireChange();
}

async function removeAction(id) {
    const idx = (await idbGet(KEY_INDEX)) || [];
    await idbSet(KEY_INDEX, idx.filter((x) => x !== id));
    await idbDel(id);
    fireChange();
}

// Public: enqueue a task-status tap when offline
export async function queueTaskStatus(runId, taskNumber, status, note) {
    return pushAction({ kind: "task-status", runId, taskNumber, status, note });
}
// Public: enqueue an attendance mark when offline
export async function queueAttendance(siteId, staffId, date, state, note) {
    return pushAction({ kind: "attendance", siteId, staffId, date, state, note });
}

async function performAction(a, apiFetch) {
    if (a.kind === "task-status") {
        return apiFetch(`/api/runs/${a.runId}/task/${a.taskNumber}`, {
            method: "PATCH", body: JSON.stringify({ status: a.status, escalation_note: a.note }),
        });
    }
    if (a.kind === "attendance") {
        return apiFetch(`/api/sites/${a.siteId}/attendance`, {
            method: "POST",
            body: JSON.stringify({ staff_id: a.staffId, date: a.date, state: a.state, note: a.note }),
        });
    }
    throw new Error("Unknown action kind: " + a.kind);
}

const BACKOFF_MS = [0, 2_000, 5_000, 15_000, 60_000, 120_000];

export async function sync(apiFetch) {
    if (!navigator.onLine) return { synced: 0, remaining: (await peek()).length, errors: [] };
    const items = await peek();
    const errors = [];
    let synced = 0;
    for (const a of items) {
        const delay = BACKOFF_MS[Math.min(a.attempts, BACKOFF_MS.length - 1)] || 0;
        if (Date.now() - (a.last_try || 0) < delay) continue;
        try {
            const res = await performAction(a, apiFetch);
            if (res.ok || (res.status >= 200 && res.status < 300)) {
                await removeAction(a.id);
                synced++;
            } else if (res.status === 409 || res.status === 400) {
                // hard conflict — drop the action but surface error
                await removeAction(a.id);
                errors.push({ id: a.id, kind: a.kind, status: res.status, note: (a.kind === "task-status" ? `Task #${a.taskNumber}` : "Attendance") });
            } else {
                await idbSet(a.id, { ...a, attempts: (a.attempts || 0) + 1, last_try: Date.now() });
            }
        } catch (e) {
            await idbSet(a.id, { ...a, attempts: (a.attempts || 0) + 1, last_try: Date.now(), last_error: String(e.message || e) });
        }
    }
    fireChange();
    return { synced, remaining: (await peek()).length, errors };
}
